"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { Check, Phone, Calendar, Clock, MapPin, Mail, User, Info, ArrowRight, ShieldCheck, Star } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
const inputClasses = "flex h-10 w-full rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
export default function BookYourCleanToday() {
  return <section id="book-your-clean-today" className="bg-gradient-to-t from-[#F9CBD6] via-[#F2AFBC] via-[#9E182B] to-[#F2E0D2] text-foreground py-16 lg:py-24 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-5">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-primary blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] rounded-full bg-secondary blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          
          <div className="lg:col-span-5 flex flex-col gap-8">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tighter mb-4 text-foreground uppercase">
                Book Your <span className="text-primary">Clean</span> Today
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed font-medium">Experience the Pure Detail difference. Whether you need a top-to-bottom deep clean, a seamless rental turnover, or a professional post-construction detail, we’re ready to get to work. Select your service below to bring our standard of pure effort and pure pride to your space.</p>
            </div>

            <div className="space-y-6">
              <h3 className="text-xl font-bold flex items-center gap-2 uppercase tracking-wide">
                <Info className="size-5 text-primary" />
                How to Schedule
              </h3>
              
              <div className="space-y-4">
                {[{
                title: "Select Your Service",
                desc: "Choose the cleaning type that fits your needs."
              }, {
                title: "Pick a Date & Time",
                desc: "Find a slot that works for your schedule."
              }, {
                title: "Relax & Enjoy",
                desc: "We handle the details with precision and pride."
              }].map((step, i) => <div key={i} data-index={i} className="flex gap-4 p-4 rounded-none bg-card border border-border/20 hover:border-primary transition-colors shadow-sm">
                    <div className="flex-shrink-0 flex items-center justify-center size-8 rounded-none bg-primary text-primary-foreground font-bold text-sm">
                      {i + 1}
                    </div>
                    <div>
                      <h4 className="font-bold text-foreground uppercase text-sm tracking-tight">{step.title}</h4>
                      <p className="text-sm text-muted-foreground">{step.desc}</p>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="flex flex-wrap gap-4 pt-4">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground bg-muted px-3 py-1.5 rounded-none border border-border/10">
                <ShieldCheck className="size-4 text-primary" />
                Licensed & Insured
              </div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground bg-muted px-3 py-1.5 rounded-none border border-border/10">
                <Star className="size-4 text-primary" />
                100% Satisfaction
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Card className="rounded-none shadow-2xl bg-card/95 backdrop-blur-sm border-none">
              <CardHeader className="pb-6">
                <CardTitle className="leading-none uppercase tracking-tighter font-bold !text-2xl">SHARE A FEW DETAILS BELOW FOR A CUSTOM QUOTE AND GET YOUR SERVICE ON THE SCHEDULE TODAY.</CardTitle>
                
              </CardHeader>
              <CardContent className="pt-6">
                <FormProvider formId="booking_form_pure_detail_booking" sectionName="booking-section" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                service_type: "",
                address: "",
                preferred_date: "",
                preferred_time: "",
                additional_notes: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                service_type: {
                  required: true
                },
                address: {
                  required: true
                },
                preferred_date: {
                  required: true
                }
              }} submitText="Request Booking" successMessage="Thank you. We will confirm with you your quote and scheduled service ASAP. Looking forward to providing Peace of mind!" errorMessage="There was a problem submitting your request. Please try again or call us directly.">
                  <BookingFormContent />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}
function BookingFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-none bg-primary/10 flex items-center justify-center text-primary mb-2 border border-primary/20">
          <Check className="size-8" />
        </div>
        <h3 className="text-2xl font-bold text-foreground uppercase tracking-tight">REQUESTED QUOTE & BOOKING RECIEVED</h3>
        <p className="text-muted-foreground max-w-md mx-auto font-medium">
          {successMessage}
        </p>
        <Button variant="outline" className="mt-6 rounded-none uppercase font-bold tracking-widest" onClick={() => window.location.reload()}>
          Book Another Service
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
            <FormLabel className="uppercase text-xs font-bold tracking-widest">Full Name <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <div className="relative">
                <User className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                <input {...field} type="text" placeholder="" className={`${inputClasses} pl-9`} />
              </div>
            </FormControl>
            <FormDescription className="text-[10px] uppercase font-bold tracking-tighter">First and last name</FormDescription>
            <FormMessage />
          </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
            <FormLabel className="uppercase text-xs font-bold tracking-widest">Email Address <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                <input {...field} type="email" placeholder="" className={`${inputClasses} pl-9`} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
            <FormLabel className="uppercase text-xs font-bold tracking-widest">Phone Number <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <div className="relative">
                <Phone className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                <input {...field} type="tel" placeholder="" className={`${inputClasses} pl-9`} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

        <FormField control={control} name="service_type" render={({
        field
      }) => <FormItem>
            <FormLabel className="uppercase text-xs font-bold tracking-widest">Service Type <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <select {...field} className={inputClasses}>
                <option value="" disabled>Select a service</option>
                <option value="Recurring Residential Clean">Recurring Residential Clean</option>
                <option value="One‑Time Deep Clean">One‑Time Deep Clean</option>
                <option value="Move‑In/Move‑Out Clean">Move‑In/Move‑Out Clean</option>
                <option value="commercial space/office clean">commercial space/office clean</option>
                <option value="Short‑Term Rental Turnover">Short‑Term Rental Turnover</option>
                <option value="post construction clean">post construction clean</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />
      </div>

      <FormField control={control} name="address" render={({
      field
    }) => <FormItem>
          <FormLabel className="uppercase text-xs font-bold tracking-widest">Service Address <span className="text-destructive">*</span></FormLabel>
          <FormControl>
            <div className="relative">
              <MapPin className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
              <input {...field} type="text" placeholder="" className={`${inputClasses} pl-9`} />
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>} />

      <div className="grid md:grid-cols-2 gap-6">
        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem>
            <FormLabel className="uppercase text-xs font-bold tracking-widest">Preferred Cleaning Date <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" className="w-full justify-start text-left font-normal rounded-none border-input h-10 px-3 hover:bg-background hover:border-primary transition-colors">
                    <SafeHydrate><Calendar className="mr-2 size-4 text-primary flex-shrink-0" /></SafeHydrate>
                    {field.value ? new Date(field.value).toLocaleDateString() : <span className="text-muted-foreground">Select a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-none border-input bg-background" align="start">
                  <DayPicker mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => {
                if (date) {
                  field.onChange(date.toISOString().split('T')[0]);
                  setDatePickerOpen(false);
                }
              }} disabled={date => date < new Date(new Date().setHours(0, 0, 0, 0))} className="bg-background border-none" />
                </PopoverContent>
              </Popover>
            </FormControl>
            <FormDescription className="text-[10px] uppercase font-bold tracking-tighter">Select a date that works for you</FormDescription>
            <FormMessage />
          </FormItem>} />

        <FormField control={control} name="preferred_time" render={({
        field
      }) => <FormItem>
            <FormLabel className="uppercase text-xs font-bold tracking-widest">Preferred Time Window</FormLabel>
            <FormControl>
              <div className="relative">
                <Clock className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                <select {...field} className={`${inputClasses} pl-9`}>
                  <option value="" disabled>Select a time window</option>
                  <option value="7am-9am">7am-9am</option>
                  <option value="9am-11am">9am-11am</option>
                  <option value="11am-1pm">11am-1pm</option>
                  <option value="1pm-3pm">1pm-3pm</option>
                  <option value="3pm-5pm">3pm-5pm</option>
                  <option value="5pm-7pm">5pm-7pm</option>
                  <option value="7pm-9pm">7pm-9pm</option>
                  <option value="After Hours">After Hours</option>
                </select>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />
      </div>

      <FormField control={control} name="additional_notes" render={({
      field
    }) => <FormItem>
          <FormLabel className="uppercase text-xs font-bold tracking-widest">Additional Details / Instructions</FormLabel>
          <FormControl>
            <textarea {...field} placeholder="Any special requests, any and all cleanings are customizable..." className={`${inputClasses} min-h-[100px] resize-y py-3`} />
          </FormControl>
          <FormMessage />
        </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none flex items-start gap-3">
          <Info className="size-5 text-destructive mt-0.5" />
          <p className="text-sm text-destructive font-bold uppercase">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-base font-bold rounded-none uppercase tracking-[0.2em] shadow-xl">
        {isSubmitting ? <>
            <span className="animate-spin mr-2"></span> Processing...
          </> : <>
            REQUEST QUOTE & BOOK YOUR CLEAN <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>
      
      <p className="text-[10px] text-center text-muted-foreground mt-4 uppercase font-bold tracking-widest opacity-60">
        By submitting this form, you agree to our privacy policy and terms of service.
      </p>
    </Form>;
}